import vm from "../main";
import axios from "./PointrCloudAxios";
import Helpers from "@/helpers/Helpers";
import ToastHelpers from "@/helpers/ToastHelpers";

export default class ContentService {
  static CONTENT_TYPES = {
    POI: "POI",
    BEACON: "BEACON",
    OBSTACLE: "OBSTACLE",
    MAP_OBJECT: "MAP_OBJECT",
    BEACON_GEOFENCE: "BEACON_GEOFENCE",
    GRAPH: "GRAPH",
    GEOFENCE: "GEOFENCE"
  };

  static async getFeatures(type, clientIdentifier, siteIdentifier, buildingInternalIdentifier, levelIndex) {
    let response;
    const endpoint = this.generateEndpoint(type);
    try {
      response = await axios.get(
        `/content/draft/clients/${clientIdentifier}/sites/${siteIdentifier}/buildings/${buildingInternalIdentifier}/levels/${levelIndex}/${endpoint}`
      );
    } catch (error) {
      console.error(error);
    }
    return response?.data;
  }

  static generateEndpoint(type) {
    let endpoint = "";
    switch (type) {
      case this.CONTENT_TYPES.POI:
        endpoint = "pois";
        break;
      case this.CONTENT_TYPES.BEACON:
        endpoint = "beacons";
        break;
      case this.CONTENT_TYPES.OBSTACLE:
        endpoint = "obstacles";
        break;
      case this.CONTENT_TYPES.MAP_OBJECT:
        endpoint = "map-objects";
        break;
      case this.CONTENT_TYPES.BEACON_GEOFENCE:
        endpoint = "beacon-geofences";
        break;
    }
    return endpoint;
  }

  static showErrorToast(response, feature, type) {
    let message;
    switch (type) {
      case this.CONTENT_TYPES.POI:
      case this.CONTENT_TYPES.OBSTACLE:
      case this.CONTENT_TYPES.MAP_OBJECT:
      case this.CONTENT_TYPES.BEACON_GEOFENCE:
      case this.CONTENT_TYPES.GEOFENCE:
      case "global_geofences":
        message = response?.message || `${vm.$t("contents.fail-message")}`;
        break;
      case this.CONTENT_TYPES.BEACON:
        message = response?.message || `${vm.$t("contents.mapDesigner.beacon-create-failed")}`;
        break;
    }
    ToastHelpers.createServiceErrorToast(response, message);
  }

  static showSuccessToast(feature, type, isEdit) {
    let message;
    let itemName;
    switch (type) {
      case this.CONTENT_TYPES.POI:
      case this.CONTENT_TYPES.OBSTACLE:
      case this.CONTENT_TYPES.MAP_OBJECT:
      case this.CONTENT_TYPES.BEACON_GEOFENCE:
      case this.CONTENT_TYPES.GEOFENCE:
      case "global_geofences":
        if (Array.isArray(feature)) {
          if (feature.length > 1) {
            itemName = `${feature.length} items`;
          } else {
            itemName = feature[0].properties.name || feature[0].properties.fid;
          }
        } else {
          itemName = feature.properties.name || feature.properties.fid;
        }
        message = `${itemName} ${isEdit ? vm.$t("contents.updated") : vm.$t("contents.created")}`;
        break;
      case this.CONTENT_TYPES.BEACON:
        message = `${
          isEdit ? vm.$t("contents.mapDesigner.beacon-updated") : vm.$t("contents.mapDesigner.beacon-created")
        }`;
        break;
    }
    ToastHelpers.createSuccessToast(message);
  }

  static async putFeature(clientInternalIdentifier, siteInternalIdentifier, features, type, isEdit) {
    let response;
    let featureCollection = {
      type: "FeatureCollection",
      features: []
    };
    features.forEach((feature) => {
      let fid = feature.properties.fid;
      if (!fid) {
        fid = Helpers.generateUuid();
        feature.properties.fid = fid;
      }
      featureCollection.features.push(feature);
    });
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.put(
        `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/features`,
        featureCollection
      );
    } catch (error) {
      console.error(error);
      this.showErrorToast(error?.response?.data, featureCollection, type);
      throw error?.response?.data?.message;
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    this.showSuccessToast(features, type, isEdit);
    return response?.data;
  }

  static async putSingleFeature(clientInternalIdentifier, feature, type, isEdit) {
    let response;
    let fid = feature.properties.fid;
    if (!fid) {
      fid = Helpers.generateUuid();
      feature.properties.fid = fid;
    }
    const featureCollection = {
      type: "FeatureCollection",
      features: [feature]
    };
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.put(
        `/content/draft/clients/${clientInternalIdentifier}/features/${fid}`,
        featureCollection
      );
    } catch (error) {
      console.error(error);
      this.showErrorToast(error?.response?.data, feature, type);
      throw error?.response?.data?.message;
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    this.showSuccessToast(feature, type, isEdit);
    return response?.data;
  }

  static async deleteFeature(clientInternalIdentifier, feature, options = {}) {
    const { showToast = true } = options;
    let response;
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.delete(
        `/content/draft/clients/${clientInternalIdentifier}/features/${feature.properties.fid}`
      );
    } catch (error) {
      console.error(error);
      ToastHelpers.createServiceErrorToast(
        error?.response?.data.error || error?.response?.data.message,
        vm.$t("contents.mapDesigner.content-delete-failed")
      );
      return;
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    if (showToast) {
      ToastHelpers.createSuccessToast(vm.$t("contents.mapDesigner.content-deleted"));
    }
    return response?.data;
  }

  static async deleteLevelFeaturesByLevelIndex(
    clientInternalIdentifier,
    siteInternalIdentifier,
    buildingInternalIdentifier,
    levelIndex
  ) {
    let response;
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.delete(
        `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/buildings/${buildingInternalIdentifier}/levels/${levelIndex}/features`
      );
    } catch (error) {
      console.error(error);
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    return response?.data;
  }

  static async getgpsGeofences(clientInternalIdentifier, siteInternalIdentifier) {
    let response;
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.get(
        `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/gps-geofences`
      );
    } catch (error) {
      console.error(error);
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    return response?.data;
  }

  static async getBeaconGeofences(clientInternalIdentifier, siteInternalIdentifier) {
    let response;
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.get(
        `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/beacon-geofences`
      );
    } catch (error) {
      console.error(error);
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    return response?.data;
  }

  static async getBeacons(clientInternalIdentifier, siteInternalIdentifier) {
    let response;
    try {
      response = await axios.get(
        `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/beacons`
      );
    } catch (error) {
      console.error(error);
    }
    return response?.data;
  }

  static async getGraphs(clientInternalIdentifier, siteInternalIdentifier) {
    let response;
    try {
      response = await axios.get(
        `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/paths`
      );
    } catch (error) {
      console.error(error);
    }
    return response?.data;
  }

  static async getSiteMapObjects(clientInternalIdentifier, siteInternalIdentifier, abortController = null) {
    let response;
    try {
      const config = abortController ? { signal: abortController.signal } : {};
      response = await axios.get(
        `/content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/map-objects`,
        config
      );
    } catch (error) {
      if (error.name === "AbortError") {
        throw error;
      }
      console.error(error);
    }
    return response?.data;
  }

  static async postGraphBySite(clientInternalIdentifier, siteInternalIdentifier, input) {
    let response;
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.post(
        `content/draft/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/paths`,
        input
      );
    } catch (error) {
      console.error(error);
      ToastHelpers.createServiceErrorToast(error?.response?.data, vm.$t("contents.mapDesigner.graph-update-fail"));
      throw error?.response?.data;
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    ToastHelpers.createSuccessToast(vm.$t("contents.mapDesigner.graph-update-success"));
    return response?.data;
  }

  static async autoGenerate(clientInternalIdentifier, siteInternalIdentifier, buildingInternalIdentifier, levelIndex) {
    let response;
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.get(
        `pws/clients/${clientInternalIdentifier}/sites/${siteInternalIdentifier}/buildings/${buildingInternalIdentifier}/levels/${levelIndex}/paths/generate`
      );
    } catch (error) {
      console.error(error);
      return error?.response?.data;
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    return response?.data;
  }

  static async uploadImage(clientInternalIdentifier, request) {
    let response;
    vm.$store.commit("ADD_LOADING_REQUEST");
    try {
      response = await axios.post(`utility/clients/${clientInternalIdentifier}/upload-image`, request);
    } catch (error) {
      console.error(error);
      return error?.response;
    } finally {
      vm.$store.commit("REMOVE_LOADING_REQUEST");
    }
    return response?.data;
  }
}
